<script setup lang="ts">
import { computed, mergeProps, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import MyAccountDialog from '@/modules/auth/components/MyAccountDialog.vue';
import { useLogout } from '@/composables/useLogout';

import type { TopNavUser } from '@/components/top_nav/types';

const { user } = defineProps<{
  user: TopNavUser;
}>();

const { t } = useI18n();
const { logout } = useLogout();

const isAccountDialogOpen = ref(false);

const userFullName = computed(() => `${user.firstName} ${user.lastName}`);
const accountButtonLabel = computed(
  () => `${t('navigation.accountDropdown.myAccount')}, ${userFullName.value}`
);
</script>

<template>
  <v-menu location="bottom end">
    <template #activator="{ props: menuProps }">
      <v-tooltip location="bottom" :text="t('navigation.accountDropdown.myAccount')">
        <template #activator="{ props: tooltipProps }">
          <v-btn
            v-bind="mergeProps(menuProps, tooltipProps)"
            icon="mdi-account"
            variant="text"
            :aria-label="accountButtonLabel"
            data-testid="account-nav-activator" />
        </template>
      </v-tooltip>
    </template>

    <v-list min-width="240" data-testid="top-nav-account-menu">
      <v-list-item
        :title="userFullName"
        :subtitle="user.email"
        data-testid="top-nav-account-menu-user" />
      <v-divider class="my-1" />
      <v-list-item
        prepend-icon="mdi-account"
        :title="t('navigation.accountDropdown.myAccount')"
        data-testid="top-nav-account-menu-my-account"
        @click="isAccountDialogOpen = true" />
      <v-list-item
        prepend-icon="mdi-logout"
        :title="t('navigation.accountDropdown.logOut')"
        data-testid="top-nav-account-menu-logout"
        @click="logout" />
    </v-list>
  </v-menu>

  <my-account-dialog
    :show-dialog="isAccountDialogOpen"
    external-activator
    @close="isAccountDialogOpen = false" />
</template>
